// src/forms/data_hub_metadata.js
// Extracted from data_hub.js — metadata loading and page metadata defaults.

import { fetch_with_timeout } from '../helpers/index';
import { build_data_hub_action_privacy_headers } from '../helpers/data_hub_privacy_policy';
import {
    append_organization_id_query_param_to_url,
    is_valid_organization_metadata
} from '../helpers/action_request_helpers';
import defaultsDeep from 'lodash.defaultsdeep';
import {
    extract_actblue_refcodes,
    get_metadata_url_params
} from '../helpers/parse_url_params';
import {
    get_iterable_ids,
    set_iterable_campaign_template_temporary_cookies,
} from '../helpers/iterable_helpers';
import { is_valid_url } from '../helpers/index';
import { scrub_url_string } from '../analytics/privacy';
import { v4 as uuidv4 } from 'uuid';

/**
 * Resolves organization_metadata using the priority cascade:
 *   1. config.organization_metadata (explicit constructor input — wins on conflict)
 *   2. window.data_hub_organization_metadata (server-injected via landing page)
 *   3. build_default_metadata (compiled into the bundle for "full bake" org-specific builds)
 *   4. {} (empty)
 *
 * Each candidate must pass is_valid_organization_metadata; invalid candidates are
 * silently dropped (caller falls through to the next tier or {}).
 *
 * @param {Object} config - data_hub_class constructor config
 * @param {Object|null} window_reference - typeof window === 'undefined' ? null : window
 * @param {Object|null} build_default_metadata - build_organization_metadata from static_vars (Mode B builds only)
 * @returns {Object} merged organization_metadata (always an object, possibly {})
 */
export function return_organization_metadata(config, window_reference, build_default_metadata) {
    const config_candidate = is_valid_organization_metadata(config?.organization_metadata)
        ? config.organization_metadata
        : null;
    const window_candidate = window_reference && is_valid_organization_metadata(window_reference.data_hub_organization_metadata)
        ? window_reference.data_hub_organization_metadata
        : null;
    const build_candidate = is_valid_organization_metadata(build_default_metadata)
        ? build_default_metadata
        : null;
    return { ...(build_candidate || {}), ...(window_candidate || {}), ...(config_candidate || {}) };
}

/**
 * True when embedded organization_metadata (e.g. landing page injection) is enough to skip GET /api/get/organization_metadata.
 * Iterable channel/message lists may still be loaded lazily elsewhere.
 * @param {Object|null|undefined} metadata
 * @returns {boolean}
 */
export function organization_metadata_is_complete_enough_to_skip_fetch(metadata) {
    if (!is_valid_organization_metadata(metadata)) {
        return false;
    }
    const forms_domain_value = (typeof metadata.data_hub_action_domain === 'string' && metadata.data_hub_action_domain.trim())
        || (typeof metadata.forms_domain === 'string' && metadata.forms_domain.trim())
        || '';

    const is_forms_domain_a_https_url_min_length = is_valid_url(forms_domain_value) && forms_domain_value.length >= 10;
    return is_forms_domain_a_https_url_min_length;
}

/**
 * Fetches public organization metadata from the forms domain.
 * @param {Object} data_hub_instance
 * @returns {Promise<Object>} The organization metadata object.
 */
export async function load_organization_public_metadata(data_hub_instance) {
    if (!data_hub_instance.forms_domain) {
        data_hub_instance.error_function?.('Error: no forms_domain', null);
        return {};
    }
    let endpoint = `${data_hub_instance.forms_domain}/api/get/organization_metadata`;
    endpoint = append_organization_id_query_param_to_url(endpoint, data_hub_instance.organization_id);
    try {
        const response = await fetch_with_timeout(endpoint, {
            method: 'GET',
            credentials: 'include',
            headers: {
                ...build_data_hub_action_privacy_headers(data_hub_instance),
            },
            timeout: 20000
        });
        if (response && response.ok) {
            const data = await response.json();
            if (data && typeof data === 'object') {
                return data;
            } else {
                data_hub_instance.error_function?.('Organization metadata is not an object: ' + typeof data, null);
            }
        } else if (response) {
            if (response.status === 403 && !data_hub_instance.organization_id) {
                data_hub_instance.error_function?.(
                    'Organization metadata returned 403 — no organization_id was provided and the backend could not resolve one from the request origin. '
                    + 'Add data-hub-organization-id to the <data-hub-signup-form> element or pass organization_id in the DataHub constructor config.',
                    null
                );
            } else {
                data_hub_instance.error_function?.('Request to get organization metadata failed: ' + response.status + response.statusText, null);
            }
        } else {
            data_hub_instance.error_function?.('Request to get organization metadata timed out', null);
        }
    } catch (error) {
        data_hub_instance.error_function?.('Error fetching organization metadata: ' + error, null);
    }
    return {};
}

/**
 * Sets page metadata defaults and Iterable IDs on the data_hub instance.
 * @param {Object} data_hub_instance
 * @param {Object} config - The constructor config object.
 */
export function set_page_metadata_defaults(data_hub_instance, config) {
    if (!config.page_metadata || typeof config.page_metadata !== 'object') {
        config.page_metadata = {};
    }

    if (!data_hub_instance.page_metadata) {
        data_hub_instance.page_metadata = Object.assign({}, config.page_metadata || {});
    } else {
        data_hub_instance.page_metadata = defaultsDeep(data_hub_instance.page_metadata, config.page_metadata || {});
    }

    // Get ActBlue refcodes.
    data_hub_instance.page_metadata.refcodes = extract_actblue_refcodes(data_hub_instance.url_params);

    // If passed in config.refcodes use as priority defaultDeep
    if (config?.refcodes) {
        data_hub_instance.page_metadata.refcodes = defaultsDeep(data_hub_instance.page_metadata.refcodes, config.refcodes);
    }

    // Get additional metadata from URL parameters.
    get_metadata_url_params(data_hub_instance.url_params, data_hub_instance);

    // Set Iterable IDs.
    const iterable_ids = get_iterable_ids(config, data_hub_instance.url_params);
    data_hub_instance.iterableCampaignId = iterable_ids.iterableCampaignId;
    data_hub_instance.iterableTemplateId = iterable_ids.iterableTemplateId;

    set_iterable_campaign_template_temporary_cookies(data_hub_instance.iterableCampaignId, data_hub_instance.iterableTemplateId);

    if (document.referrer) {
        // Scrub PII (email, h_* HMACs, tokens, etc.) from the referrer before it lands on the
        // instance. Form submits send page_metadata.referer to the backend, and error tracking
        // can include it in logged payloads — both should not be carrying a referring URL's
        // raw query params. utm_*, refcode, and other safe params are preserved by the
        // scrubber.
        data_hub_instance.page_metadata.referer = scrub_url_string(document.referrer);
    }
    // TODO 260512 can we use the same analytics functions to set these better? is it worth extracting the og image etc here too?
    if (document.title) {
        data_hub_instance.page_metadata.page_title = document.title;
    }
    if (window.location.href) {
        data_hub_instance.page_metadata.page_url = window.location.href.split('?')[0];
    }

    // Set both names. The analytics session module (initialize_analytics_session, runs later
    // via init_analytics_bootstrap) will read whichever exists and re-write both to the same
    // value, keeping page_metadata.pageview_id (canonical, new) and page_metadata.pageview_uuid
    // (legacy alias for actblue refcodes) in sync from the very first pageview.
    const initial_pageview_id = uuidv4();
    data_hub_instance.page_metadata.pageview_id = initial_pageview_id;
    data_hub_instance.page_metadata.pageview_uuid = initial_pageview_id;
}
